import type { ReactNode } from "react";
import type { IMetricHelp } from "../components/shared/MetricInfo/MetricInfo.types";

export const METRIC_HELP: Record<string, IMetricHelp> = {
  THROUGHPUT: {
    measures: "Count of work items moved to a closed / resolved state.",
    calculated:
      "Sum of closed items across the selected teams within the window.",
    howToRead:
      "Steady or rising is good; a sharp drop signals a delivery stall.",
  },
  "COMMITMENT RELIABILITY": {
    measures:
      "How much of what a team committed to at sprint start actually shipped.",
    calculated:
      "Average of (committed items closed ÷ committed items) across teams.",
    howToRead:
      "80–95% is healthy. ~100% can mean under-committing; <70% means over-committing.",
  },
  "CYCLE TIME": {
    measures: "How long an item takes once work actively starts.",
    calculated:
      "Mean business days between the Active and Resolved transitions.",
    howToRead: "Lower is better. A rising trend means flow is congesting.",
  },
  "COMPLETED WORK / 1-EFFORT ITEM": {
    measures: "Real hours behind one unit of estimated effort.",
    calculated:
      "Logged hours ÷ effort points completed, averaged across teams.",
    howToRead:
      "Lower / stable is better. Drift means estimates are decoupling from reality.",
  },
  "CARRY-OVER RATE": {
    measures: "Share of planned work that spills into the next sprint.",
    calculated: "Average of (carried items ÷ planned items).",
    howToRead:
      "Under 10% is good; over 20% points to over-planning or blockers.",
  },
  VELOCITY: {
    measures: "Total story points completed.",
    calculated: "Sum of effort points on completed items.",
    howToRead:
      "A capacity trend, not a target — compare a team to itself, never across teams.",
  },
  "TOKEN SPEND": {
    measures: "Total AI tokens used by the selected teams.",
    calculated: "Sum of input + output tokens across all models.",
    howToRead:
      "Treated as an adoption signal (rising = more usage). Read alongside Dollar Spend and delivery output.",
  },
  "DOLLAR SPEND": {
    measures: "USD cost of AI usage.",
    calculated: "Token usage priced per model, summed over the window.",
    howToRead:
      'Judge against output delivered — rising spend is fine when velocity / throughput rise with it. Shows "no data" when $0.',
  },
  "BUG DENSITY INDEX": {
    measures: "Total Bugs and Defects opened across the whole organization.",
    calculated:
      "Count of work items of type Bug or Defect whose Created Date falls in the selected range (the range spans min–max Created Date; sprint / iteration is ignored).",
    howToRead:
      "Organization-wide — the Team filter does not scope this. Lower is better; normalize against org size when trending.",
  },
  "DEFECT ESCAPE RATE": {
    measures: "Share of bugs found by the customer rather than internally.",
    calculated:
      "Escaped ÷ total Bugs × 100, over Bugs created in the range. Escaped = Work Item Type Bug with FoundBy = Customer. Only Bugs count (Defects are excluded from this ratio); sprint is ignored.",
    howToRead:
      "Organization-wide. Target near 0% — the single strongest quality signal.",
  },
  "REWORK RATIO": {
    measures: "How often work bounces back after it reached Testing.",
    calculated:
      "Reworked ÷ total × 100, per sprint. An item (PBI, Bug or Defect) is 'reworked' if its state transitioned back to Active after reaching Testing. Sprints whose iteration end date falls inside the selected interval are included.",
    howToRead:
      "Per team. Lower is better; a spike signals weak acceptance criteria or review.",
  },
  "CRITICAL / BLOCKER BUGS": {
    measures: "Volume of the most severe items.",
    calculated:
      "Count of Bug or Defect with Severity '1 - Critical' or '2 - High' whose Created Date falls in the range (sprint ignored).",
    howToRead:
      "Organization-wide. Zero is the goal; any sustained count needs attention.",
  },
  "FAILED SCENARIOS / ITEM": {
    measures: "Average failed test scenarios attached to a work item.",
    calculated:
      "Average count of 'Not Ok' outcomes parsed from the TestNotes field, per work item (Bug, Defect, PBI), per sprint. Sprints whose iteration end date falls inside the selected interval are included.",
    howToRead:
      "Per team. Lower is better; rising means tests are catching regressions late.",
  },
  "NON-PRODUCTION DEFECTS": {
    measures:
      "Bugs and Defects intercepted internally rather than by the customer.",
    calculated:
      "Count of Bugs/Defects that were NOT escaped (FoundBy ≠ Customer) whose Created Date falls in the range (sprint ignored).",
    howToRead:
      "Organization-wide. Read next to Defect Escape Rate — together they split the window's bugs into caught-internally vs escaped.",
  },
  "FEATURE DELIVERY RATE": {
    measures: "Number of features delivered in the window.",
    calculated: "Sum of completed Feature-type work items across teams.",
    howToRead:
      "Higher is better; read against Carry-Over to confirm features are truly finishing.",
  },
  "RE-RELEASE FREQUENCY": {
    measures: "How often a release has to be re-shipped to fix it.",
    calculated: "Count of hotfix / re-release events per quarter.",
    howToRead: "Lower is better. (Awaiting instrumentation.)",
  },
  "CODE QUALITY SCORE": {
    measures: "A composite health score for delivered code.",
    calculated: "Weighted blend of defect, rework and coverage signals.",
    howToRead: "Higher is better. (Awaiting instrumentation.)",
  },
  "PR CYCLE TIME": {
    measures: "Hours from a PR being opened to being merged.",
    calculated: "Median of (merged_at − created_at).",
    howToRead:
      "Lower is better; long tails indicate review bottlenecks. (Coming soon.)",
  },
  "AVG COMMITS / PR": {
    measures: "Average commits contained in a PR.",
    calculated: "Total commits ÷ PRs.",
    howToRead:
      "Very high counts can signal scope creep in a single PR. (Coming soon.)",
  },
  "NUMBER OF PRs": {
    measures: "Total pull requests opened.",
    calculated: "Count of PRs in the window.",
    howToRead: "A throughput signal; read with PR Cycle Time. (Coming soon.)",
  },
  "TEST FILES IN PRs": {
    measures: "Share of PRs that touch test files.",
    calculated: "PRs with ≥1 test file ÷ total PRs.",
    howToRead: "Higher is better — a proxy for test discipline. (Coming soon.)",
  },
};

export const CHART_HELP: Record<string, IMetricHelp> = {
  "Throughput & Velocity — Achieved per Sprint": {
    measures:
      "What the selected teams actually delivered in each sprint, with the AI spend of that same sprint drawn over it.",
    calculated:
      "Bars are the closed items and the effort behind them, per sprint. The line sums the daily AI cost (or tokens) of the days between the sprint's start and end dates, over the selected teams only. No target or baseline is applied — these are raw numbers.",
    howToRead:
      "Read the bars and the line together: spend rising while the bars stay flat is the signal worth investigating. The two axes are independent, so the line crossing a bar means nothing. Sprints overlapping in time share their days, so per-sprint spend can add up to more than the period total. Date-Range mode only.",
  },
  "Throughput by Team": {
    measures: "Closed-item throughput over time (single team) or by team.",
    calculated:
      "One bar per sprint in Date Range mode; grouped current-vs-prior bars per team in comparison modes; stacked by team when several teams are selected.",
    howToRead:
      "Look for a stable or rising profile. This is the only chart that also works in Rolling 3-Month and Year-over-Year modes.",
  },
  "Avg Resolution Days (Cycle Time)": {
    measures: "Average business days to resolve, by sprint or by team.",
    calculated:
      "One bar per sprint over the selected date range; stacked by team when several teams are selected.",
    howToRead:
      "Lower bars are better. Date-Range mode only — unlike Throughput by Team, this chart has no Rolling / Year-over-Year comparison.",
  },
  "Bug & Defect Severity Split": {
    measures: "Monthly bug counts broken out by severity (P1–P4).",
    calculated: "Stacked bars per month, one segment per severity band.",
    howToRead:
      "Watch the P1/P2 (red/orange) segments — those should stay thin. Date-Range mode only.",
  },
  "Avg Test Cases / Item by Team": {
    measures: "Test coverage intent — test cases authored per work item.",
    calculated: "Average test cases ÷ item, per team per month.",
    howToRead:
      "Higher generally indicates stronger test discipline. Date-Range mode only.",
  },
  "Feature Delivery by Team": {
    measures: "Features delivered over time, by team.",
    calculated: "Stacked (multi-team) or single-series bars per month.",
    howToRead:
      "Look for a consistent delivery cadence rather than end-of-quarter spikes. Date-Range mode only.",
  },
  "PR Cycle Time by Team (hours)": {
    measures: "PR cycle time distribution per team.",
    calculated: "Median created→merged hours per team.",
    howToRead: "Lower and tighter is better. (Coming soon.)",
  },
};

export type SectionNoteKey =
  | "summary"
  | "velocity"
  | "quality"
  | "organizationQuality"
  | "delivery"
  | "prhealth";

export const SECTION_NOTES: Record<SectionNoteKey, ReactNode> = {
  summary: (
    <>
      How much work the selected teams are completing and how fast. Read these
      together: healthy delivery is{" "}
      <strong>steady Throughput and Velocity</strong> with{" "}
      <strong>flat or falling Cycle Time and Carry-Over</strong>. Token &amp;
      Dollar Spend sit here as an adoption signal — rising spend is only a
      concern if delivery is not moving with it.
    </>
  ),
  velocity: (
    <>
      How much work the selected teams are completing and how fast. Read these
      together: healthy delivery is{" "}
      <strong>steady Throughput and Velocity</strong> with{" "}
      <strong>flat or falling Cycle Time and Carry-Over</strong>.
    </>
  ),
  quality: (
    <>
      Defect signals for the selected teams — every metric here is{" "}
      <strong>lower-is-better</strong>. Rework Ratio and Failed Scenarios / Item
      are <strong>per-team and sprint-based</strong> — sprints whose iteration
      end date falls inside the range are included. The chart below is{" "}
      <strong>Date-Range-only</strong> — it does not follow Rolling 3-Month or
      Year-over-Year.
    </>
  ),
  organizationQuality: (
    <>
      Organization-wide defect signals — every metric here is{" "}
      <strong>lower-is-better</strong> and computed across{" "}
      <strong>all teams and all area paths</strong>, regardless of the Team
      filter above. Bug Density Index, Defect Escape Rate, Critical / Blocker
      Bugs and Non-Production Defects are computed from{" "}
      <strong>Created Date</strong> (sprint is ignored). Read Bug Density
      alongside Defect Escape Rate: a low Bug Density with a high Defect Escape
      Rate means bugs are slipping to production rather than being caught early.
      The chart below is <strong>Date-Range-only</strong> — it does not follow
      Rolling 3-Month or Year-over-Year.
    </>
  ),
  delivery: (
    <>
      Feature-level output for the selected teams. Feature Delivery Rate is
      live; Re-Release Frequency and Code Quality Score are{" "}
      <strong>awaiting backend instrumentation</strong>. The chart below is{" "}
      <strong>Date-Range-only</strong> — no Rolling 3-Month or Year-over-Year
      comparison.
    </>
  ),
  prhealth: (
    <>
      Pull-request flow and review health. This section is{" "}
      <strong>not yet instrumented</strong> — the cards are placeholders and are
      not connected to the Team filter or the date modes yet.
    </>
  ),
};

export const getMetricHelp = (label: string): IMetricHelp | undefined =>
  METRIC_HELP[label.toUpperCase()];

export const getChartHelp = (title: string): IMetricHelp | undefined =>
  CHART_HELP[title];
